import React, { useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useDispatch } from 'react-redux';
import { AmplifyLogs } from '@constants';
import { logOutAction } from '@store/actions/logout.actions';
import { auth, config } from '../../../../../src/config';
import { clearCookie } from '@utils/cookieHelper';
import { callDeleteOkta } from '@services/AccountConnectFormService';
import { logUserEvent } from '@analytics/amplifyLogging';
import { ButtonBase, DialogBase, StackBase, TypographyBase } from '@fmc-digital/ui';
import Lottie from 'lottie-react';
import { bell_animation } from './BellAnimation';
import { Container, useMediaQuery, useTheme } from '@mui/material';

const IdleTimerContainer: React.FC = () => {
    const dispatch = useDispatch();
    const [modalOpen, setModalOpen] = useState(false);
    const sessionTimeOutRef = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.only('xs'));

    const handleOnIdle = () => {
        setModalOpen(true);
        sessionTimeOutRef.current = setTimeout(handleLogOut, Number(config.env('sessionTimeout')) * 60 * 1000);
    };

    useIdleTimer({
        timeout: Number(config.env('sessionIdleTime')) * 60 * 1000,
        events: ['mousemove', 'keydown', 'mousewheel', 'mousedown', 'touchstart', 'touchmove'],
        onIdle: handleOnIdle
    });

    const handleStayIn = (eventLog?: any) => {
        if (eventLog) {
            logUserEvent({
                type: eventLog.type,
                description: eventLog.description,
                action: eventLog.action
            });
        }
        setModalOpen(false);
        clearTimeout(sessionTimeOutRef.current);
    };

    const handleLogOut = async () => {
        setModalOpen(false);
        clearTimeout(sessionTimeOutRef.current);
        dispatch(logOutAction());
        try {
            await auth.getOkta().signOut();
            auth.getOkta().tokenManager.clear();
            await callDeleteOkta(config.env('oktaBaseUrl'));
            clearCookie();
        } catch (error) {
            logUserEvent({
                type: 'Session logout',
                description: `Logout failed during session timeout with ${JSON.stringify(error)}`,
                action: 'LogoutFailedDuringSessionTimeout'
            });
        } finally {
            auth.getOkta().tokenManager.clear();
            clearCookie();
        }
    };
    const renderActions = () => {
        return (
            <StackBase
                direction="column"
                sx={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    gap: '1rem'
                }}
            >
                <ButtonBase
                    variant="contained"
                    color="primary"
                    onClick={() => handleStayIn(AmplifyLogs.idleTimerModal)}
                    data-testid="primary-button"
                    sx={{
                        padding: '1rem 2rem',
                        width: ['100%', 'auto']
                    }}
                >
                    Stay In Session
                </ButtonBase>
            </StackBase>
        );
    };

    const renderContent = () => {
        const style = {
            width: '9.375rem',
            height: '9.375rem'
        };

        return (
            <StackBase direction="column" gap="2rem" alignItems="center">
                <Lottie animationData={bell_animation} loop autoplay style={style} />
                {isMobile ? (
                    <TypographyBase variant="paragraph" color="surface.dark" textAlign={'center'}>
                        Your session is about to time out. <br />
                        You will be logged out soon.
                    </TypographyBase>
                ) : (
                    <TypographyBase variant="paragraph" color="surface.dark" textAlign={'center'}>
                        Your session is about to time out. You will be logged out soon.
                    </TypographyBase>
                )}
            </StackBase>
        );
    };

    return (
        <DialogBase
            renderDialogContent={renderContent}
            handleClose={() => handleStayIn()}
            open={modalOpen}
            title="Timeout Warning"
            renderDialogActions={renderActions}
        />
    );
};

export default IdleTimerContainer;
